import { useTranslation } from "react-i18next";
import {
  AddButton,
  ButtonsContainer,
  CancelButton,
} from "../addClientModal/addClientModal.styles";
import { useContext, useEffect, useState } from "react";
import { UserContext } from "../../../contexts/user.context";
import { GeneralObj } from "../../../types/company.types";
import {
  Delete,
  Get,
  Post,
  SendFormData,
  handleError,
} from "../../../utils/fetch.util";
import { FilePond } from "react-filepond";
import { AppsType } from "../../../routes/admin/dashboard/apps/Apps.route";
import Select from "react-select";

type EditAppModalProps = {
  isOpen: boolean;
  AppId: number;
  onClose: () => void;
  getAllApps: () => void;
};

export default function EditAppModal({
  isOpen,
  onClose,
  AppId,
  getAllApps,
}: EditAppModalProps) {
  const DEFAULT_App: AppsType = {
    id: 0,
    name: "",
    description: "",
    picture_url: "",
    leango_app_service_relationship: [],
  };
  const [logoUrl, setLogoUrl] = useState<any[]>([]);
  const [app, setApp] = useState<AppsType | GeneralObj>(DEFAULT_App);
  const [services, setServices] = useState<any[]>([]);
  const [servicesToAdd, setServicesToAdd] = useState<any[]>([]);
  const [servicesToRemove, setServicesToRemove] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { getValidToken } = useContext(UserContext);
  const { t } = useTranslation();

  const appServices =
    app?.leango_app_service_relationship?.map((service: any) => service.asdc_client_service.asdc_client_service_id) ||
    [];

    console.log("appServices", app);

  const servicesOptions =
    services?.map((service: any) => ({
      value: service.asdc_client_service_id,
      label: service.asdc_client_service_name,
    })) || [];

  const appServicesValue = servicesOptions.filter(
    (option: any) =>
      (appServices.includes(option.value) &&
        !servicesToRemove.includes(option.value)) ||
      servicesToAdd.includes(option.value)
  );

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setApp({ ...app, [e.target.name]: e.target.value });
  };

  const editApp = async (e: any) => {
    e.preventDefault();
    setIsLoading(true);
    const formData = new FormData();
    formData.append("name", app.name);
    formData.append("description", app.description);

    const logoUrlModified =
      logoUrl?.length > 0 && logoUrl[0]?.file !== undefined;

    if (logoUrlModified) {
      logoUrl.forEach((item) => formData.append("picture_url", item.file));
    }

    const res = await SendFormData(
      `leango-apps/update-leango-app?leango_app_id=${AppId}`,
      formData,
      getValidToken(),
      "PATCH"
    );

    if (res.error || !res.data) {
      handleError(res);
      setIsLoading(false);
      return;
    }

    if (servicesToAdd.length > 0) {
      const addServices = await Post(
        "leango-apps/link-leango-app-to-service",
        {
          leango_app_id: AppId,
          asdc_client_service_ids: servicesToAdd,
        },
        getValidToken()
      );

      if (addServices.error || !addServices.data) {
        handleError(addServices);
        setIsLoading(false);
        return;
      }
    }

    if (servicesToRemove.length > 0) {
      const query = [];
      query.push(`leango_app_id=${AppId}`);
      query.push(`asdc_client_service_ids=[${servicesToRemove.join(",")}]`);
      const removeServices = await Delete(
        `leango-apps/unlink-leango-app-from-service?${query.join("&")}`,
        getValidToken()
      );

      if (removeServices.error || !removeServices.data) {
        handleError(removeServices);
        setIsLoading(false);
        return;
      }
    }

    getAllApps();
    setIsLoading(false);
    onClose();
    setApp(DEFAULT_App);
    setLogoUrl([]);
    setServicesToAdd([]);
    setServicesToRemove([]);
  };

  useEffect(() => {
    const getAllApps = async () => {
      const res = await Get("leango-apps/get-all-leango-apps", getValidToken());

      const singleApp = res.data.filter((app: AppsType) => {
        if (app?.id === AppId) {
          return app;
        }
      });

      setApp(singleApp[0]);
      setLogoUrl(
        singleApp[0].picture_url
          ? [`${import.meta.env.VITE_API_URL}${singleApp[0].picture_url}`]
          : []
      );
    };

    const getAllServices = async () => {
      const res = await Get(
        "asdc-client-service/all-services",
        getValidToken()
      );

      setServices(res.data);
    };

    setServicesToAdd([]);
    setServicesToRemove([]);
    getAllApps();
    getAllServices();
  }, [AppId, getValidToken]);

  return (
    <>
      <form
        onSubmit={editApp}
        className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white w-[500px] p-6 !z-[999999] ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      >
        <h2 className="text-3xl mb-6 !text-black">{t("contract__edit_app")}</h2>
        <div className="flex flex-col gap-2 mt-5">
          <label>{t("structure_app_name")}</label>
          <input
            value={app?.name}
            name="name"
            className="border"
            type="text"
            onChange={handleChange}
            required
          />
        </div>
        <div className="flex flex-col gap-2 mt-5">
          <label>{t("structure_app_description")}</label>
          <input
            value={app?.description == null ? "" : app?.description}
            name="description"
            className="border"
            type="text"
            onChange={handleChange}
            required
          />
        </div>
        <div className="flex flex-col gap-2 mt-5">
          <label>{t("structure_app_logo_url")}</label>
          <FilePond
            files={logoUrl}
            onupdatefiles={(files) => {
              setLogoUrl(files);
            }}
            allowMultiple={false}
            allowReorder
            allowImagePreview
            allowImageExifOrientation
            maxFiles={1}
            name="files"
            labelIdle='Drag & Drop your files or <span class="filepond--label-action">Browse</span>'
            className={"!mb-0"}
            acceptedFileTypes={["image/png", "image/jpeg"]}
          />
        </div>

        <div className="flex flex-col gap-2 mt-5">
          <label>{t("structure_app_services")}</label>
          <Select
            options={servicesOptions}
            isMulti
            onChange={(e, action) => {
              if (
                action.action === "remove-value" &&
                appServices.includes(action.removedValue.value)
              ) {
                setServicesToRemove((prev) => [
                  ...prev,
                  action.removedValue.value,
                ]);
              }
              if (
                action.action === "remove-value" &&
                !appServices.includes(action.removedValue.value)
              ) {
                setServicesToAdd((prev) =>
                  prev.filter(
                    (service) => service !== action.removedValue.value
                  )
                );
              }
              if (
                action.action === "select-option" &&
                !appServices.includes(action.option?.value)
              ) {
                setServicesToAdd((prev) => [...prev, action.option?.value]);
              }
              if (
                action.action === "select-option" &&
                appServices.includes(action.option?.value)
              ) {
                setServicesToRemove((prev) =>
                  prev.filter((service) => service !== action.option?.value)
                );
              }
              if (action.action === "clear" && appServices.length > 0) {
                setServicesToAdd([]);
                setServicesToRemove([...appServices]);
              }
              if (action.action === "clear" && appServices.length === 0) {
                setServicesToAdd([]);
                setServicesToRemove([]);
              }
            }}
            value={appServicesValue}
            placeholder={t("structure_app_select_services")}
            className="border"
          />
        </div>

        <ButtonsContainer style={{ gap: "2rem" }}>
          <AddButton
            isLoading={isLoading}
            type="submit"
            style={{ background: "black", color: "white" }}
          >
            {t("contract__edit_app")}
          </AddButton>
          <CancelButton
            type="button"
            style={{ border: "2px solid black" }}
            onClick={() => {
              setServicesToAdd([]);
              setServicesToRemove([]);
              onClose();
              setLogoUrl([]);
              setApp(DEFAULT_App);
            }}
          >
            {t("cancelBtn")}
          </CancelButton>
        </ButtonsContainer>
      </form>
      <div
        className={`bg-black ${
          isOpen ? "opacity-60" : "opacity-0 pointer-events-none"
        } fixed inset-0 z-[99999] flex items-center justify-center transition-all duration-500`}
      ></div>
    </>
  );
}
